import axios from "axios";
import { toast } from "../components/ui/sonner";
import i18n from "../i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// The access token cookie only lives 15 minutes. Without this, any request
// made after being idle for a bit longer fails with 401 and forces a full
// re-login even though the refresh_token cookie (7 days) is still valid.
// This interceptor transparently refreshes the access token once and
// retries the original request; concurrent 401s share a single in-flight
// refresh call instead of each firing their own.
let refreshPromise = null;
// Guards against showing the "signed in elsewhere" toast/redirect more than
// once when several in-flight requests all fail with the same reason at
// roughly the same time (e.g. multiple autosave calls).
let sessionInvalidatedHandled = false;
// Only the true auth-FLOW endpoints should skip the refresh-and-retry logic
// below (retrying them would either loop or make no sense). This must NOT
// include /auth/me: a plain startsWith("/auth/") check previously matched
// it too, so a 401 from /auth/me (access_token cookie expired after 15min,
// which happens on every cold app/PWA relaunch) bailed straight to
// /login instead of trying the still-valid 30-day refresh_token first -
// breaking "remember me" on every relaunch.
const NO_REFRESH_RETRY_PATHS = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/logout", "/auth/logout-all"];

function handleSessionInvalidated(messageKey = "auth.sessionInvalidated") {
  if (sessionInvalidatedHandled) return;
  sessionInvalidatedHandled = true;
  toast.error(i18n.t(messageKey));
  setTimeout(() => {
    window.location.href = "/login";
  }, 1800);
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const isAuthEndpoint = NO_REFRESH_RETRY_PATHS.some((p) => config?.url?.startsWith(p));
    if (response?.data?.detail === "account_disabled") {
      handleSessionInvalidated("auth.accountDisabled");
      return Promise.reject(error);
    }
    if (response?.status !== 401 || config._retriedAfterRefresh || isAuthEndpoint) {
      return Promise.reject(error);
    }
    // A session revoked server-side (logout, "sign out everywhere", or an
    // admin action) will also fail the refresh call below, so skip
    // straight to the dedicated message instead of looping through a
    // refresh attempt that's guaranteed to fail.
    if (response?.data?.detail === "session_invalidated") {
      handleSessionInvalidated();
      return Promise.reject(error);
    }
    config._retriedAfterRefresh = true;
    try {
      if (!refreshPromise) {
        refreshPromise = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true }).finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;
      return api(config);
    } catch (refreshError) {
      if (refreshError.response?.data?.detail === "session_invalidated") {
        handleSessionInvalidated();
      } else if (!config?.url?.startsWith("/auth/me")) {
        // /auth/me is the bootstrap "am I logged in" check fired on every
        // app load, including by users who were never logged in at all (no
        // refresh_token cookie exists yet). Forcing a hard reload to
        // /login here - instead of just letting checkAuth()'s own catch
        // set user=false - caused a self-inflicted infinite reload loop
        // on the /login page itself for any logged-out visitor. Every
        // OTHER protected call still hard-redirects on a dead refresh.
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return i18n.t("common.genericError");
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
